import React, { useState } from "react";
import { Pencil, Trash2, Lock, User, Users, Building2, UserCheck, X } from "lucide-react";

/**
 * NoteCard — rendering di una singola meeting_note nel pannello/lista.
 *
 * Props:
 *   note: {id, author_name, body, created_at, updated_at, is_mine,
 *          meta.share_mode, shared_with?}
 *   currentUserName: stringa (per mostrare "Tu" se is_mine)
 *   onEdit(note): apre composer in modalità edit
 *   onDelete(note): chiama DELETE API
 */
function fmtRel(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = Date.now();
    const diffMin = Math.floor((now - d.getTime()) / 60000);
    if (diffMin < 1) return "ora";
    if (diffMin < 60) return `${diffMin} min fa`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} h fa`;
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function ShareChip({ mode, staffCount, clientCount, hidden }) {
    if (hidden) {
        // W3 FIX 2026-11-04 — riceventi non-autori vedono chip neutro
        return (
            <span
                data-testid="note-share-chip-hidden"
                style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "3px 8px", background: "#EFEFEC", color: "#5F6560",
                    borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase",
                    fontWeight: 600,
                }}
            >
                <UserCheck size={11} /> Condivisa con te
            </span>
        );
    }
    const config = {
        private:           { icon: <Lock size={11} />,        label: "Privata",                   bg: "#EFEFEC", fg: "#5F6560" },
        specific_staff:    { icon: <User size={11} />,        label: `Staff (${staffCount || 0})`, bg: "#DBEAFE", fg: "#1E40AF" },
        all_participants:  { icon: <Users size={11} />,       label: "Tutti i partecipanti",      bg: "#DCFCE7", fg: "#166534" },
        specific_clients:  { icon: <Building2 size={11} />,   label: `Cliente (${clientCount || 0})`, bg: "#FEF3C7", fg: "#92400E" },
        mixed:             { icon: <UserCheck size={11} />,   label: "Staff + Cliente",           bg: "#FCE7F3", fg: "#9D174D" },
    };
    const c = config[mode] || config.private;
    return (
        <span
            data-testid={`note-share-chip-${mode}`}
            style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "3px 8px", background: c.bg, color: c.fg,
                borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase",
                fontWeight: 600,
            }}
        >
            {c.icon} {c.label}
        </span>
    );
}

export default function NoteCard({ note, currentUserName, onEdit, onDelete }) {
    const [confirmDel, setConfirmDel] = useState(false);
    const edited = note.updated_at && note.created_at
        && new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 2000;
    const authorLabel = note.is_mine ? "Tu" : (note.author_name || "—");
    const staffCount = note.shared_with?.staff_user_ids?.length || 0;
    const clientCount = note.shared_with?.client_user_ids?.length || 0;

    return (
        <div
            data-testid={`note-card-${note.id}`}
            style={{
                padding: "14px 16px", background: "#FFFFFF",
                border: "1px solid #EEEFEA", borderRadius: 4,
                display: "flex", flexDirection: "column", gap: 8,
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: "#181B20" }} data-testid={`note-card-author-${note.id}`}>
                        {authorLabel}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#8A8F88", marginTop: 1 }}>
                        {fmtRel(note.created_at)}
                        {edited && <span style={{ marginLeft: 6, fontStyle: "italic" }}>
                            · modificata {fmtRel(note.updated_at)}
                        </span>}
                    </div>
                </div>
                <ShareChip
                    mode={note.meta?.share_mode}
                    staffCount={staffCount}
                    clientCount={clientCount}
                    hidden={!note.is_mine && !note.meta}
                />
            </div>
            <div style={{ fontSize: 14, color: "#181B20", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {note.body}
            </div>
            {note.is_mine && !confirmDel && (
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", opacity: 0.6 }}>
                    <button
                        className="lt-icon-btn"
                        onClick={() => onEdit?.(note)}
                        aria-label="Modifica"
                        data-testid={`note-edit-${note.id}`}
                    >
                        <Pencil size={13} />
                    </button>
                    <button
                        className="lt-icon-btn"
                        onClick={() => setConfirmDel(true)}
                        aria-label="Elimina"
                        data-testid={`note-delete-${note.id}`}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            )}
            {confirmDel && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 10px", background: "#FEF3C7", borderRadius: 3 }}>
                    <span style={{ fontSize: 12, color: "#92400E" }}>Eliminare questa nota?</span>
                    <div style={{ display: "flex", gap: 6 }}>
                        <button
                            className="lt-btn-secondary"
                            onClick={() => setConfirmDel(false)}
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            data-testid={`note-delete-cancel-${note.id}`}
                        >
                            <X size={11} /> Annulla
                        </button>
                        <button
                            onClick={() => onDelete?.(note)}
                            style={{ padding: "4px 10px", background: "#8C2C38", color: "#FFF", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                            data-testid={`note-delete-confirm-${note.id}`}
                        >
                            Elimina
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
